import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import { useForkRef } from '@mui/material/utils';
import type { SystemStyleObject } from '@mui/system';
import React from 'react';

import { Neutrals } from '../../color-palette/colors';
import { PROSE_MAX } from '../Screen/screenLayout';

export type EmptyStateAlign = 'center' | 'start';

export interface EmptyStateProps {
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  headingLevel?: 'h1' | 'h2' | 'p';
  align?: EmptyStateAlign;
  titleRef?: React.Ref<HTMLElement>;
  sx?: SxProps<Theme>;
}

const ROOT_SX: Record<EmptyStateAlign, SystemStyleObject<Theme>> = {
  center: { alignItems: 'center', textAlign: 'center', px: 3 },
  start: { alignItems: 'flex-start', textAlign: 'start', px: 0 },
};

const baseSx: SystemStyleObject<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: 2,
  py: 5,
  color: Neutrals[600],
};

const iconSx: SystemStyleObject<Theme> = { color: Neutrals[400], display: 'flex' };
const titleSx: SystemStyleObject<Theme> = { '&:focus:not(:focus-visible)': { outline: 'none' } };
const descriptionSx: SystemStyleObject<Theme> = { color: Neutrals[600], maxInlineSize: PROSE_MAX };
const actionSx: SystemStyleObject<Theme> = { mt: 1 };

export const EmptyState = React.forwardRef<HTMLDivElement, EmptyStateProps>(
  (
    { icon, title, description, action, headingLevel = 'p', align = 'center', titleRef, sx },
    ref
  ) => {
    const handleTitleRef = useForkRef<HTMLElement>(titleRef);
    return (
      <Box ref={ref} sx={[baseSx, ROOT_SX[align], ...(Array.isArray(sx) ? sx : [sx])]}>
        {icon && <Box sx={iconSx}>{icon}</Box>}
        <Typography
          ref={handleTitleRef}
          variant="cardTitleSm"
          component={headingLevel}
          tabIndex={titleRef ? -1 : undefined}
          sx={titleSx}
        >
          {title}
        </Typography>
        {description && (
          <Typography variant="label" component="p" sx={descriptionSx}>
            {description}
          </Typography>
        )}
        {action && <Box sx={actionSx}>{action}</Box>}
      </Box>
    );
  }
);

EmptyState.displayName = 'EmptyState';
